/*  ==========================================================================
    MAIN
    Everything that can not be defined as a module, utility, etc. comes into
    this file. For example: google maps initialisation in the footer section.

    INFO:
    - code will be loaded dynamically by the "inline.js"
    - generated file will be found here: "/Resources/Public/admin/js/main.js"
    - write a clean code!
    - try to keep a clean structure, some sections (general, header, vista...)
    are predefined

    HELP:
    - https://developer.mozilla.org/de/docs/Web/JavaScript
    - http://wesbos.com/javascript-modules/
    ========================================================================== */

/*
 * IMPORTS
 */

// node modules imports
import $ from 'jquery';
import 'animatescroll.js';
import 'jquery.cookie/jquery.cookie.js';
import 'jquery-inview';
import 'lazysizes';
import 'lodash/lodash.js';
import 'objectFitPolyfill';
import moment from 'moment/src/moment';
import 'moment/src/locale/de';
import 'moment/src/locale/it';
import 'mresize';
import 'picturefill';
import vimeoPlayer from '@vimeo/player';
import 'waypoints/lib/jquery.waypoints.js';
import WebFont from 'webfontloader';

// local imports
import * as modules from './modules/modules';
import * as vars from './globals/variables';
import * as utils from './utilities/utils';
import {
    onFirstUserAction,
} from './utilities/first-user-interaction';
import {
    initUserRecognition, isUserKnownInThisSession,
} from './utilities/user-session-recognition';
import './ehotelier/dynforms/init.js';
import './ehotelier/general/e-mail-validation.js';


/* GENERAL
 * --------------------------------------------------------------------------- */

/*
 * DEV TOOLS ADVERTISING
 */

utils.general.initDevToolsAdv();

/*
 * DYNAMIC FILE LOADING
 */

// css
utils.dynFileLoader.load(vars.css_global, 'css');

// js
utils.dynFileLoader.load(vars.js_global, 'js');

/**
 * TRACKING (USERCENTRICS + GOOGLE TAG MANAGER)
 * procedure: first load "usercentrics", then "google tag manager" and lastly
 *   init the GTM conversions and events.
 * condition: if the user is for the first time on the website, then load the
 *   stuff after the first user interaction. Is he recurring, then load it ASAP.
 */

const loadUserCentrics = () => {
    utils.dynFileLoader.load({
        userCentrics: {
            src: 'https://web.cmp.usercentrics.eu/ui/loader.js',
            attributes: {
                'id': 'usercentrics-cmp',
                'data-settings-id': vars.ts.usercentrics.uc_license_id,
            },
            onLoad: () => {
                /* eslint-disable */
                /* Google Consent Mode */
                let dataLayer;
                window.dataLayer = dataLayer = window.dataLayer || [];
                function gtag() {
                    dataLayer.push(arguments);
                }

                // set "denied" as default for some measurement settings
                gtag('consent', 'default', {
                    ad_user_data: 'denied',
                    ad_storage: 'denied',
                    ad_personalization: 'denied',
                    analytics_storage: 'denied',
                    wait_for_update: 2000, // milliseconds to wait for update
                });

                // Enable ads data redaction by default [optional]
                gtag('set', 'ads_data_redaction', true);
                /* End Google Consent Mode */

                // load gtm
                (function (w, d, s, l, i) {
                    w[l] = w[l] || []; w[l].push({
                        'gtm.start':
                            new Date().getTime(),
                        'event': 'gtm.js',
                    }); const f = d.getElementsByTagName(s)[0],
                        j = d.createElement(s), dl = l != 'dataLayer' ? `&l=${l}` : ''; j.async = true;
                    j.src = `https://brandnamic.tech/g/gtm/gtm.js?id=${i}${dl}`; f.parentNode.insertBefore(j, f);
                }(window, document, 'script', 'dataLayer', vars.ts.google.gtm.id));

                // thank you pages
                const currentPageId = $('body').attr('data-page-id');
                if (currentPageId === vars.ts.pids.success_request) {
                    dataLayer.push({
                        "event": "BnEventConversion",
                        "eventCategory": "Conversion",
                        "eventAction": "Request"
                    });
                } else if (currentPageId === vars.ts.pids.success_quick_request) {
                    dataLayer.push({
                        "event": "BnEventConversion",
                        "eventCategory": "Conversion",
                        "eventAction": "Quick-Request"
                    });
                } else if (currentPageId === vars.ts.pids.success_request_event) {
                    dataLayer.push({
                        "event": "BnEventConversion",
                        "eventCategory": "Conversion",
                        "eventAction": "Event-Request"
                    });
                } else if (currentPageId === vars.ts.pids.success_newsletter) {
                    dataLayer.push({
                        "event": "BnEventConversion",
                        "eventCategory": "Conversion",
                        "eventAction": "Newsletter"
                    });
                } else if (currentPageId === vars.ts.pids.success_catalog) {
                    dataLayer.push({
                        "event": "BnEventConversion",
                        "eventCategory": "Conversion",
                        "eventAction": "Catalog"
                    });
                } else if (currentPageId === vars.ts.pids.success_meeting_request) {
                    dataLayer.push({
                        "event": "BnEventConversion",
                        "eventCategory": "Conversion",
                        "eventAction": "Request-Tagung"
                    });
                }
            },
        },
    }, 'js');
};

if (!isUserKnownInThisSession()) {
    onFirstUserAction()
        .then(loadUserCentrics);
} else {
    loadUserCentrics();
}

/*
 * LOAD FONTS (needs "webfontloader.js")
 */

WebFont.load({
    active: function () {

        // show full text in "body" after webfonts are rendered
        vars.$body.removeClass('no-font');

    },
    custom: {
        families: vars.fonts,
        urls: [
            '/typo3conf/ext/bn_typo_dist/Resources/Public/admin/assets/fonts/style.css'
        ]
    }
});

/*
 * RESOLUTION INFO
 */

utils.general.initResolutionInfo();

/*
 * AUTO SCROLL OFFSET
 */

// only applies on mobile view or for header types 1 and 2)
let autoScrollOffset = 0;
if (vars.$body.hasClass('mobile-view') || vars.$p_header.hasClass('bn-header--v1') || vars.$p_header.hasClass('bn-header--v2')) {
    autoScrollOffset = utils.general.initHeaderOffset();
}

/*
 * AUTO SCROLL TO MAIN
 */

if (vars.$body.hasClass('scroll-to-main')) {
    if (!vars.$body.hasClass('vista-none')) {
        $(window).on('load', function () {
            vars.$p_main.animatescroll({
                easing: 'easeInOutSine',
                padding: autoScrollOffset,
                scrollSpeed: 500
            });
        });
    }
}

/*
 * ANCHOR SCROLL
 */

if(window.location.hash) {
    let anchorScrollTarget = window.location.hash;
    if ($(anchorScrollTarget).length) {
        $(window).scrollTop(0);
        $(window).on('load', function () {
            $(anchorScrollTarget).animatescroll({
                easing: 'easeInOutSine',
                padding: autoScrollOffset,
                scrollSpeed: 500
            });
        });
    }
}

/*
 * NEWSLETTER CONFIRM
 */

if (vars.$body.hasClass('uid14')) {
    let bnnewsletterconfirm = new brandnamic_newsletterconfirm({
        'onerror': function(message) {
            if(vars.$body.hasClass('be-user')) {
                console.log('');
                console.log('----------------------------------------------------------------------------------------------------');
                console.log('NEWSLETTER-CONFIRM:');
                console.log('Error: '+window.location.href);
                console.log('----------------------------------------------------------------------------------------------------');
                console.log('');
            }
        },
        'onsuccess': function() {
            if(vars.$body.hasClass('be-user')) {
                console.log('');
                console.log('----------------------------------------------------------------------------------------------------');
                console.log('NEWSLETTER-CONFIRM:');
                console.log('Success: '+window.location.href);
                console.log('----------------------------------------------------------------------------------------------------');
                console.log('');
            }
        },
        'serverurl': 'https://admin.ehotelier.it/bnlocalproxy.php'
    });
}

/*
 * 404
 */

if (vars.$body.hasClass('uid8')) {

    // auto redirect to home after some time
    $(window).on('load', function () {
        setTimeout(function () {
            window.location.href = vars.domain;
        }, 5000);
    });

}


/* HEADER
 * --------------------------------------------------------------------------- */

/*
 * MAIN MENU (NON MOBILE!)
 */

let $h_mainMenu = vars.$p_header.find('.bn-main-menu');

// activate prevent link on touch on first level links (only for main menu on non mobile views!)
$h_mainMenu.find('.bn-main-menu__item--has-children > .bn-main-menu__link--lvl-0').each(function () {
    $(this).addClass('bn-main-menu__prevent-link-on-touch');
});

// activate prevent link on touch on second and third level links (only for main menu on non mobile views and on specific header versions!)
if (vars.$p_header.hasClass('bn-header--v3') || vars.$p_header.hasClass('bn-header--v4')) {
    $h_mainMenu.find('.bn-main-menu__sub-menu .bn-main-menu__item--has-children > .bn-main-menu__link').each(function () {
        $(this).addClass('bn-main-menu__prevent-link-on-touch');
    });
}

// initialize util
utils.preventLinkOnTouch.init('bn-main-menu__prevent-link-on-touch');

/*
 * DESKTOP SIDEBAR MENU
 */

// open/close desktop sidebar menu (only applies for header type 3)
if (vars.$p_header.hasClass('bn-header--v3')) {
    let $h_sidebar = vars.$p_header.find('.bn-sidebar'),
        $h_sidebarTrigger = vars.$p_header.find('.bn-sidebar-trigger');
        $h_sidebarTrigger.on('click', function () {
        $h_sidebarTrigger.toggleClass('bn-sidebar-trigger--act');
        $h_sidebar.toggleClass('bn-sidebar--open');
        $('body').toggleClass('js-menu-open');
    });
}

/*
 * MOBILE MENU
 */

// open/close mobile menu
let $mh_controlBar = vars.$p_mobileHeader.find('.bn-control-bar'),
    $mh_controlBarTrigger = vars.$p_mobileHeader.find('.bn-control-bar-trigger');
$mh_controlBarTrigger.on('click', function () {
    $mh_controlBarTrigger.toggleClass('bn-control-bar-trigger--act');
    $mh_controlBar.toggleClass('bn-control-bar--open');
});

// open/close mobile menu for footer v3
let $mf_controlBarTrigger = vars.$p_footer.find('.bn-control-bar-trigger');
$mf_controlBarTrigger.on('click', function () {
    $mf_controlBarTrigger.toggleClass('bn-control-bar-trigger--act');
    $mh_controlBar.toggleClass('bn-control-bar--open');
});

// close mobile menu on click outside of menu
$mh_controlBar.on('click', function (e) {
    if (!$(e.target).parents('.bn-control-bar__row').length) {
        $mh_controlBarTrigger.removeClass('bn-control-bar-trigger--act');
        $mh_controlBar.removeClass('bn-control-bar--open');
    }
});

// open/close sub menu
let $mh_subMenuTrigger = vars.$p_mobileHeader.find('.bn-sub-menu-trigger');
$mh_subMenuTrigger.on('click', function (e) {
    e.preventDefault();
    $(this).toggleClass('bn-sub-menu-trigger--act');
    $(this).parents('.bn-main-menu__item').find('.bn-main-menu__sub-menu').toggleClass('bn-main-menu__sub-menu--open');
});

/*
 * PRINT LOGO
 */

// trigger lazysizes
$(window).on('load', function () {
    vars.$p_header.find('.bn-main-logo.bn-main-logo--print img').addClass('lazypreload');
});


/* VISTA
 * --------------------------------------------------------------------------- */

/*
 * SEASONS
 */

let $v_seasons = vars.$p_vista.find('.bn-seasons');
if ($v_seasons.length) {

    // initialize slider
    utils.slickCarousel.initSingleSlider($v_seasons);

    // responsive video sources (for local videos)
    utils.general.initRespVideoSrc($v_seasons);

    // local cinemagraphs
    if ($v_seasons.find('.bn-seasons__item.bn-seasons__item--video-local-cinemagraph').length) {
        $v_seasons.find('.bn-seasons__item--video-local-cinemagraph video').each(function () {
            let seasonsVideo = $(this);

            //object fit fix for older browsers, especially ie11
            objectFitPolyfill(seasonsVideo);

        });
    }

    // local normal videos
    if ($v_seasons.find('.bn-seasons__item.bn-seasons__item--video-local-normal').length) {
        $v_seasons.find('.bn-seasons__item.bn-seasons__item--video-local-normal').each(function () {
            let $overlay = $(this).find('.bn-seasons__overlay'),
                video = $(this).find('video').get(0);

            // start
            $overlay.on('click', function () {
                $overlay.addClass('hidden');
                if ($v_seasons.hasClass('slick-slider')) {
                    $v_seasons.slick('slickPause');
                }
                video.play();
            });

            // pause or end
            $(video).bind('pause ended', function() {
                $overlay.removeClass('hidden');
                if ($v_seasons.hasClass('slick-slider')) {
                    $v_seasons.slick('slickPlay');
                }
            });

        });
    }

    // youtube
    if ($v_seasons.find('.bn-seasons__item.bn-seasons__item--video-youtube').length) {

        // get youtube iframe api js and create a callback
        let dynFileConfig_youtube = {
            js_youtube: {
                callback: function () {

                    // get all youtube videos
                    let playerInfoList = [];
                    $v_seasons.find('.bn-seasons__item.bn-seasons__item--video-youtube iframe').each(function () {
                        let $this = $(this),
                            iframeId = $this.attr('id');
                        playerInfoList.push(
                            {
                                id: iframeId
                            },
                        );
                    });

                    // function which gets called by the youtube api
                    function onYouTubeIframeAPIReady() {

                        // go through all videos
                        if(typeof playerInfoList !== 'undefined') {
                            for(let i = 0; i < playerInfoList.length;i++) {
                                let curPlayer = new YT.Player(playerInfoList[i].id, {
                                    events: {
                                        'onReady': window.onYoutubeReady,
                                        'onStateChange': window.onYouTubePlayerStateChange
                                    }
                                });
                            }
                        }

                    }
                    window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;

                    // on play function
                    function onYoutubeReady() {
                    }
                    window.onYoutubeReady = onYoutubeReady;

                    // on state change function
                    function onYouTubePlayerStateChange(event) {
                        switch (event.data) {
                            case YT.PlayerState.UNSTARTED:
                                if ($v_seasons.hasClass('slick-slider')) {
                                    $v_seasons.slick('slickPause');
                                }
                                break;
                            case YT.PlayerState.ENDED:
                                if ($v_seasons.hasClass('slick-slider')) {
                                    $v_seasons.slick('slickPlay');
                                }
                                break;
                            case YT.PlayerState.PLAYING:
                                if ($v_seasons.hasClass('slick-slider')) {
                                    $v_seasons.slick('slickPause');
                                }
                                break;
                            case YT.PlayerState.PAUSED:
                                if ($v_seasons.hasClass('slick-slider')) {
                                    $v_seasons.slick('slickPlay');
                                }
                                break;
                        }
                    }
                    window.onYouTubePlayerStateChange = onYouTubePlayerStateChange;


                },
                src: vars.js_youtubeIframeApi
            }
        };
        utils.dynFileLoader.load(dynFileConfig_youtube, 'js');

    }

    // vimeo
    if ($v_seasons.find('.bn-seasons__item.bn-seasons__item--video-vimeo').length) {

        // get vimeo player api js and create a callback
        let dynFileConfig_vimeo = {
            js_vimeo: {
                callback: function () {

                    // get all vimeo videos
                    $v_seasons.find('.bn-seasons__item.bn-seasons__item--video-vimeo iframe').each(function () {
                        let $this = $(this),
                            iframeId = $this.attr('id'),
                            player = new Vimeo.Player(iframeId);

                        // on bufferstart event
                        player.on('bufferstart', function() {
                            if ($v_seasons.hasClass('slick-slider')) {
                                $v_seasons.slick('slickPause');
                            }
                        });

                        // on play event
                        player.on('play', function() {
                            if ($v_seasons.hasClass('slick-slider')) {
                                $v_seasons.slick('slickPause');
                            }
                        });

                        // on pause event
                        player.on('pause', function() {
                            if ($v_seasons.hasClass('slick-slider')) {
                                $v_seasons.slick('slickPlay');
                            }
                        });

                        // on ended event
                        player.on('ended', function() {
                            if ($v_seasons.hasClass('slick-slider')) {
                                $v_seasons.slick('slickPlay');
                            }
                        });

                    });

                },
                src: vars.js_vimeoPlayerApi
            }
        };
        utils.dynFileLoader.load(dynFileConfig_vimeo, 'js');

    }

}

/*
 * SCROLL DOWN
 */

let $v_scrollDown = vars.$p_vista.find('.bn-scroll-down');
$v_scrollDown.on('click', function () {
    vars.$p_main.animatescroll({
        easing: 'easeInOutSine',
        padding: autoScrollOffset,
        scrollSpeed: 500
    });
});


/* MAIN
 * --------------------------------------------------------------------------- */

/*
 * BN TEXT PLUGIN (for "imprint" and "privacy")
 */

let $bnTextPlugin = vars.$p_main.find('.bn-text-plugin');
$bnTextPlugin.each(function () {
    let $bnTextPlugin_serviceContainer = $(this).find('.bn-text-plugin__service');
    if ($bnTextPlugin_serviceContainer) {
        let bnTextPlugin_id = $bnTextPlugin_serviceContainer.attr('data-id'),
            bnTextPlugin_lang = $bnTextPlugin_serviceContainer.attr('data-lang');
        function bnTextPluginRequestListener() {
            $bnTextPlugin_serviceContainer.empty().append(this.responseText);
        }
        let bnTextPlugin_request = new XMLHttpRequest();
        bnTextPlugin_request.addEventListener('load', bnTextPluginRequestListener);
        bnTextPlugin_request.open('GET', 'https://api.text.bnamic.com/render/channels/' + bnTextPlugin_lang + '/' + bnTextPlugin_id + '/textblockshtml');
        bnTextPlugin_request.send();
    }
});

/*
 * WAYPOINT 1
 */

// offset
let wp1Offset = utils.general.initHeaderOffset();

// recalculate offset on resize
vars.$page.on('mresize', _.debounce(function () {
    wp1Offset = utils.general.initHeaderOffset();
}, 500));

// settings
function wp1Settings() {
    return new Waypoint({
        element: vars.$wp_1,
        enabled: true,
        handler: function (direction) {
            if (direction === 'down') {

                // minimize header (only applies to header types 1, 2 and 3)
                if (vars.$p_header.hasClass('bn-header--v1') || vars.$p_header.hasClass('bn-header--v2') || vars.$p_header.hasClass('bn-header--v3')) {
                    vars.$p_header.addClass('bn-header--minimized');
                }

                // minimize mobile header
                vars.$p_mobileHeader.addClass('bn-mobile-header--minimized');

                // show fixed top references
                vars.$p_footer.find('.bn-top-references').addClass('bn-top-references--visible');

                // activate scroll top
                vars.$p_scrollUp.addClass('bn-scroll-up--activated');

                // make quick request v2 hideable
                vars.$p_quickRequestV2.addClass('bn-quick-request--hideable');

            } else if (direction === 'up') {

                // enlarge header (only applies to header types 1, 2 and 3)
                if (vars.$p_header.hasClass('bn-header--v1') || vars.$p_header.hasClass('bn-header--v2') || vars.$p_header.hasClass('bn-header--v3')) {
                    vars.$p_header.removeClass('bn-header--minimized');
                }

                // enlarge mobile header
                vars.$p_mobileHeader.removeClass('bn-mobile-header--minimized');

                // hide fixed top references
                vars.$p_footer.find('.bn-top-references').removeClass('bn-top-references--visible');

                // deactivate scroll top
                vars.$p_scrollUp.removeClass('bn-scroll-up--activated');

                // make quick request v2 not hideable
                vars.$p_quickRequestV2.removeClass('bn-quick-request--hideable');

            }
        },
        offset: wp1Offset
    });
}

// initialization
let $init_wp1 = wp1Settings();

// reinitialization after resize (to get correct offset)
vars.$page.on('mresize', _.debounce(function () {
    $init_wp1 = wp1Settings();
}, 500));


/* MODULES
 * --------------------------------------------------------------------------- */

// additional list view
let $m_additionalsListView = vars.$p_main.find('.bn-additionals-list-view');
$m_additionalsListView.each(function () {
    let $this = $(this);

    // module
    modules.additionalsListView.initAdditionalsListView($this);

});

// buttons
let $m_buttons = vars.$p_main.find('.bn-buttons');
$m_buttons.each(function () {
    let $this = $(this);

    // module
    modules.buttons.initButtons($this);

});

// contentcluster article
let $m_ccArticle = vars.$p_main.find('.bn-cc-article');
$m_ccArticle.each(function () {
    let $this = $(this);

    // module
    modules.ccArticle.initCcArticle($this);

});

// contentcluster list view
let $m_ccListView = vars.$p_main.find('.bn-cc-list-view');
$m_ccListView.each(function () {
    let $this = $(this);

    // module
    modules.ccListView.initCcListView($this);

});

// download list
let $m_downloadList = vars.$p_main.find('.bn-download-list');
$m_downloadList.each(function () {
    let $this = $(this);

    // module
    modules.downloadList.initDownloadList($this);

});

// downloadboxes
let $m_downloadboxes = vars.$p_main.find('.bn-downloadboxes');
$m_downloadboxes.each(function () {
    let $this = $(this);

    // module
    modules.downloadboxes.initDownloadboxes($this);

});

// freerooms-list-view
let $m_freeroomsListView = vars.$p_main.find('.bn-freerooms-list-view');
$m_freeroomsListView.each(function () {
    let $this = $(this);

    // module
    modules.freeroomsListView.initFreeroomsListView($this);

});

// gallery
let $m_gallery = vars.$p_main.find('.bn-gallery');
$m_gallery.each(function () {
    let $this = $(this);

    // module
    modules.gallery.initGallery($this);

});

// images
let $m_images = vars.$p_main.find('.bn-images');
$m_images.each(function () {
    let $this = $(this);

    // module
    modules.images.initImages($this);

});

// impressions
let $m_impressions = vars.$p_main.find('.bn-impressions');
$m_impressions.each(function () {
    let $this = $(this);

    // module
    modules.impressions.initImpressions($this);

});

// impressions v2
let $m_impressionsV2 = vars.$p_main.find('.bn-impressions-v2');
$m_impressionsV2.each(function () {
    let $this = $(this);

    // module
    modules.impressionsV2.initImpressionsV2($this);

});

// impressions v3
let $m_impressionsV3 = vars.$p_main.find('.bn-impressions-v3');
$m_impressionsV3.each(function () {
    let $this = $(this);

    // module
    modules.impressionsV3.initImpressionsV3($this);

});

// impressions v4
let $m_impressionsV4 = vars.$p_main.find('.bn-impressions-v4');
$m_impressionsV4.each(function () {
    let $this = $(this);

    // module
    modules.impressionsV4.initImpressionsV4($this);

});

// offer detail
let $m_offerDetail = vars.$p_main.find('.bn-offer-detail');
$m_offerDetail.each(function () {
    let $this = $(this);

    // module
    modules.offerDetail.initOfferDetail($this);

});

// offers list view
let $m_offersListView = vars.$p_main.find('.bn-offers-list-view');
$m_offersListView.each(function () {
    let $this = $(this);

    // module
    modules.offersListView.initOffersListView($this);

});

// offers list view v2
let $m_offersListViewV2 = vars.$p_main.find('.bn-offers-list-view-v2');
$m_offersListViewV2.each(function () {
    let $this = $(this);

    // module
    modules.offersListViewV2.initOffersListViewV2($this);

});

// offers teaser
let $m_offersTeaser = vars.$p_main.find('.bn-offers-teaser');
$m_offersTeaser.each(function () {
    let $this = $(this);

    // module
    modules.offersTeaser.initOffersTeaser($this);

});

// price overview
let $m_priceOverview = vars.$p_main.find('.bn-price-overview');
$m_priceOverview.each(function () {
    let $this = $(this);

    // module
    modules.priceOverview.initPriceOverview($this);

});

// room detail
let $m_roomDetail = vars.$p_main.find('.bn-room-detail');
$m_roomDetail.each(function () {
    let $this = $(this);

    // module
    modules.roomDetail.initRoomDetail($this);

});

// rooms list view
let $m_roomsListView = vars.$p_main.find('.bn-rooms-list-view');
$m_roomsListView.each(function () {
    let $this = $(this);

    // module
    modules.roomsListView.initRoomsListView($this);

});

// rooms list view v2
let $m_roomsListViewV2 = vars.$p_main.find('.bn-rooms-list-view-v2');
$m_roomsListViewV2.each(function () {
    let $this = $(this);

    // module
    modules.roomsListViewV2.initRoomsListViewV2($this);

});

// rooms list view v3
let $m_roomsListViewV3 = vars.$p_main.find('.bn-rooms-list-view-v3');
$m_roomsListViewV3.each(function () {
    let $this = $(this);

    // module
    modules.roomsListViewV3.initRoomsListViewV3($this);

});

// route planner
let $m_routePlanner = vars.$p_main.find('.bn-route-planner');
$m_routePlanner.each(function () {
    let $this = $(this);

    // module
    modules.routePlanner.initRoutePlanner($this);

});

// slide box
let $m_slideBox = vars.$p_main.find('.bn-slide-box');
$m_slideBox.each(function () {
    let $this = $(this);

    // module
    modules.slideBox.initSlideBox($this);

});

// text
let $m_text = vars.$p_main.find('.bn-text');
$m_text.each(function () {
    let $this = $(this);

    // module
    modules.text.initText($this);

});

// text pic box
let $m_textPicBox = vars.$p_main.find('.bn-text-pic-box');
$m_textPicBox.each(function () {
    let $this = $(this);

    // module
    modules.textPicBox.initTextPicBox($this);

});

// text pic box v2
let $m_textPicBoxV2 = vars.$p_main.find('.bn-text-pic-box-v2');
$m_textPicBoxV2.each(function () {
    let $this = $(this);

    // module
    modules.textPicBoxV2.initTextPicBoxV2($this);

});

// text pic box v3
let $m_textPicBoxV3 = vars.$p_main.find('.bn-text-pic-box-v3');
$m_textPicBoxV3.each(function () {
    let $this = $(this);

    // module
    modules.textPicBoxV3.initTextPicBoxV3($this);

});

// text pic box v4
let $m_textPicBoxV4 = vars.$p_main.find('.bn-text-pic-box-v4');
$m_textPicBoxV4.each(function () {
    let $this = $(this);

    // module
    modules.textPicBoxV4.initTextPicBoxV4($this);

});

// text pic box v4
let $m_weatherDetail = vars.$p_main.find('.bn-weather-detail');
$m_weatherDetail.each(function () {
    let $this = $(this);

    // module
    modules.weatherDetail.initWeatherDetail($this);

});

// linkbox
let $m_linkBoxes = vars.$p_main.find('.bn-linkboxes');
$m_linkBoxes.each(function () {
    let $this = $(this);

    // module
    modules.linkBoxes.initLinkBoxes($this);

});

// linkbox v2
let $m_linkBoxesV2 = vars.$p_main.find('.bn-linkboxes-v2');
$m_linkBoxesV2.each(function () {
    let $this = $(this);

    // module
    modules.linkBoxesV2.initLinkBoxesV2($this);

});


/* FOOTER
 * --------------------------------------------------------------------------- */

/*
 * BOXES
 */

let $f_box = vars.$p_footer.find('.bn-f-top__box');
if($f_box.length) {

    // accordion function (only on smaller mobile devices needed)
    utils.accordions.initSimpleAccordion($f_box);

}

/*
 * WEATHER
 */

let $f_weatherTeaser = vars.$p_footer.find('.bn-weather-teaser');
if($f_weatherTeaser.length) {

    // hook function - v1
    function funcWeatherTeaserV1Hook(container,data) {
    }

    // hook function - v2
    function funcWeatherTeaserV2Hook(container,data) {
    }

    // custom functions - v1
    function funcWeatherTeaserV1Custom(container,data) {
    }

    // custom functions - v2
    function funcWeatherTeaserV2Custom(container,data) {
    }

    // ehotelier service initialization (if element is in view)
    $f_weatherTeaser.one('inview', function() {
        let eh_weatherTeaserContainer = $f_weatherTeaser.find('.eh-service'),
            eh_weatherTeaserConfig = {
                addParams: {},
                container: eh_weatherTeaserContainer,
                type: 'weather',
                country:  vars.weather_country,
                location: vars.weather_location,
                provider: vars.weather_provider
            };
        if ($f_weatherTeaser.hasClass('bn-weather-teaser--v1')) {

            // teaser V1
            eh_weatherTeaserConfig.funcCustom = funcWeatherTeaserV1Custom;
            eh_weatherTeaserConfig.funcHook = funcWeatherTeaserV1Hook;
            eh_weatherTeaserConfig.subtype = 'teaser';
            utils.ehotelierLoader.initWeatherService(eh_weatherTeaserConfig);

        } else if ($f_weatherTeaser.hasClass('bn-weather-teaser--v2')) {

            // teaser V2
            eh_weatherTeaserConfig.funcCustom = funcWeatherTeaserV2Custom;
            eh_weatherTeaserConfig.funcHook = funcWeatherTeaserV2Hook;
            eh_weatherTeaserConfig.subtype = 'teaser-v2';
            utils.ehotelierLoader.initWeatherService(eh_weatherTeaserConfig);

        }
    });
}

/*
 * MAP
 */

let $f_mapCanvas = vars.$p_footer.find('.bn-google-maps');
if($f_mapCanvas.length) {

    // initialize a simple map
    utils.maps.initSimpleMap($f_mapCanvas);

}

/*
 * TOP REFERENCES
 */

let $f_topReferences = vars.$p_footer.find('.bn-top-references');

// show/hide fixed top references items on click
$f_topReferences.on('click', '.bn-top-references__item--trigger', function () {
    $(this).toggleClass('bn-top-references__item--act');
    $f_topReferences.find('.bn-top-references__container').toggleClass('bn-top-references__container--visible');
});

/*
 * VIEW SWITCHER
 */

let $f_viewSwitcher = vars.$p_footer.find('.bn-view-switcher');
if($f_viewSwitcher.length) {

    // check if "bn-view-switched" cookie exists and add class if it is so
    if ($.cookie('bn-view-switched')) {
        vars.$body.addClass('switched-view');
    }

    // show switcher on desktop if view got switched
    if (vars.$body.hasClass('switched-view')) {
        $f_viewSwitcher.addClass('bn-view-switcher--view-switched');
    } else {
        $f_viewSwitcher.removeClass('bn-view-switcher--view-switched');
    }

    // trigger web
    $f_viewSwitcher.find('.bn-view-switcher__trigger--web').on('click', function(e) {
        e.preventDefault();
        $.cookie('bn-view-switched', 1, {path: '/'});
        setTimeout(function(){
            window.location.reload();
        },500);
    });

    // trigger mobile
    $f_viewSwitcher.find('.bn-view-switcher__trigger--mobile').on('click', function(e) {
        e.preventDefault();
        $.removeCookie('bn-view-switched', {path: '/'});
        setTimeout(function(){
            window.location.reload();
        },500);
    });

}


/* MISC
 * --------------------------------------------------------------------------- */

/*
 * SCROLL UP
 */

if(vars.$p_scrollUp.length) {
    let st_oldPos = 0;

    // set element visible on upscroll
    $(window).scroll(function(event){
        let st_newPos = $(this).scrollTop();
        if (st_newPos > st_oldPos) {

            // downscroll
            vars.$p_scrollUp.removeClass('bn-scroll-up--visible');
            vars.$p_quickRequestV2.removeClass('bn-quick-request--hide');

        } else {

            // upscroll
            vars.$p_scrollUp.addClass('bn-scroll-up--visible');
            vars.$p_quickRequestV2.addClass('bn-quick-request--hide');

        }
        st_oldPos = st_newPos;
    });

    // trigger on click
    vars.$p_scrollUp.on('click', function () {
        vars.$body.animatescroll({
            easing: 'easeInOutSine',
            scrollSpeed: 500
        });
    });

}

/*
 * QUICK REQUEST V1
 */

if(vars.$p_quickRequestV1.length) {

    // function to show/hide quick request
    function transformQuickRequestV1(event, isInView) {
        if (isInView) {
            vars.$p_quickRequestV1.addClass('bn-quick-request--visible');
        } else {
            //setTimeout(function () {
                if (!vars.$body.hasClass('vista-none')) {
                    if ($(window).scrollTop() <= 80) {
                        vars.$p_quickRequestV1.removeClass('bn-quick-request--visible');
                    }
                }
            //}, 1000);
        }
    }

    // show quick request if main is in view
    vars.$p_main.on('inview', function(event, isInView) {
        transformQuickRequestV1(event, isInView);
    });

    // show quick request if footer is in view
    vars.$p_footer.on('inview', function(event, isInView) {
        transformQuickRequestV1(event, isInView);
    });

}

/*
 * QUICK REQUEST V2
 */

if(vars.$p_quickRequestV2.length) {
    let $quickRequestTitle = vars.$p_quickRequestV2.find('.bn-quick-request__title'),
        $quickRequestForm = vars.$p_quickRequestV2.find('.bn-quick-request__form');

    // show form on click
    $quickRequestTitle.on('click', function () {
        $quickRequestTitle.toggleClass('bn-quick-request__title--act');
        $quickRequestForm.toggleClass('bn-quick-request__form--visible');
    });

}


const addEventListenerList = (list, event, fn) => {
    for (let i = 0, len = list.length; i < len; i++) {
        list[i].addEventListener(event, fn, false);
    }
};
if (vars.$p_header.hasClass('bn-header--v3') || vars.$p_header.hasClass('bn-header--v4')) {
    const hoverMenuItem = e => {
        const parentMenuItem = e.target.closest('.bn-main-menu__item');
        const submenu = parentMenuItem.querySelectorAll('.bn-main-menu__lvl')[0];
        submenu.style.paddingTop = parentMenuItem.offsetTop + 'px';
    };
    const menuItems = document.querySelectorAll('.bn-main-menu__lvl--0 > li > a');
    addEventListenerList(menuItems, 'mouseover', hoverMenuItem);
}

/*
 * COOKIE BANNER
 */

/* TRACKING
 * --------------------------------------------------------------------------- */

/*
 * GTM
 */

if (vars.$body.hasClass('gtm--on')) {

    // click to call
    vars.$body.on('click', '.gtm-click-to-call', function () {
        dataLayer.push({
            'event': 'BnEventConversion',
            'eventAction': 'ClickToCall',
            'eventCategory': 'Conversion'
        });
    });

    // general click events
    vars.$body.on('click', '.gtm-event', function () {
        let $this = $(this),
            eventAction = $this.attr('data-action'),
            eventCategory = $this.attr('data-category'),
            eventLabel = $this.attr('data-label');
        dataLayer.push({
            'event': 'BnEvent',
            'eventAction': eventAction,
            'eventCategory': eventCategory,
            'eventLabel': eventLabel
        });
    });

    // image zoom event
    vars.$body.on('click', '.mfp--single .mfp__item, .mfp--gallery .mfp__item', function () {
        let $this = $(this),
            eventLabel = $this.attr('href');
        dataLayer.push({
            'event': 'BnEvent',
            'eventAction': 'Image Zoom',
            'eventCategory': 'Click',
            'eventLabel': eventLabel
        });
    });

    // offer filter event
    vars.$body.on('click', '.bn-offers-filter .bn-offers-filter__submit', function () {
        let $this = $(this),
            categories,
            fromDate = $this.parents('.bn-offers-filter').find('input.bn-offers-filter__date--from').val(),
            toDate = $this.parents('.bn-offers-filter').find('input.bn-offers-filter__date--to').val();

        // get categories
        categories = new Array();
        $this.parents('.bn-offers-filter').find('input.bn-offers-filter__cat:checked').each(function () {
            let category = $(this).val();
            categories.push(category);
        });

        // fire event
        dataLayer.push({
            'event': 'BnEvent',
            'eventAction': 'Offer Filter',
            'eventCategory': 'Click',
            'eventLabel': 'Period: '+fromDate+' - '+toDate+' | Categories: '+categories
        });

    });

    // route planner event
    vars.$body.on('click', '.bn-route-planner .bn-route-planner__submit', function () {
        let $this = $(this),
            eventLabel = $this.parents('.bn-route-planner').find('input.bn-route-planner__start').val();
        dataLayer.push({
            'event': 'BnEvent',
            'eventAction': 'Route Planner',
            'eventCategory': 'Click',
            'eventLabel': 'Starting point: '+eventLabel
        });
    });

    // rte link event
    vars.$body.on('click', '.bn-rte-link', function () {
        let $this = $(this),
            eventLabel = $this.attr('href');
        dataLayer.push({
            'event': 'BnEvent',
            'eventAction': 'Inlink',
            'eventCategory': 'Click',
            'eventLabel': eventLabel
        });
    });


}
window.addEventListener('scroll', function (event) {
    if(window.pageYOffset  > 0){
        $('.dna-sidelink').removeClass('dna-sidelink--visible');
    } else {
        if(!document.body.classList.contains('vista-none'))
            $('.dna-sidelink').addClass('dna-sidelink--visible');
    }
});
if(document.body.classList.contains('vista-none')){
    $('.dna-sidelink').removeClass('dna-sidelink--visible');
}

let $popup =  $('.bn-popup');
$popup.on( "click", ".js--close", function() {
    $popup.toggleClass('hidden');
});

// if(!sessionStorage.getItem('popup') && $popup.length) {
//     $('.bn-popup').removeClass('hidden');
//     sessionStorage.setItem("popup", "1");
// }
if($popup.length) {
    $('.bn-popup').removeClass('hidden');
    sessionStorage.setItem("popup", "1");
}

/*
 * iFrame adjustments for accessibility
 */
setTimeout(function () {
    document.querySelectorAll('iframe:not([title])').forEach(function (iframe) {
        var styleAttr = iframe.getAttribute('style') || '';
        var computedHeight = window.getComputedStyle(iframe).height;

        var isInvisible =
            iframe.offsetHeight === 0 ||
            styleAttr.indexOf('height:0') !== -1 ||
            computedHeight === '0px';

        if (isInvisible) {
            iframe.setAttribute('title', 'Technical iFrame (hidden)');
            iframe.setAttribute('aria-hidden', 'true');
        }
    });
}, 3000);


// WEBPACK FOOTER //
// ./source/js/main.js